"use client";

import { useCallback, useMemo, useRef } from "react";
import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Checkbox } from "@/app/components/ui/checkbox";
import { cn } from "@/lib/utils";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "../../../ui/validation-popover";

/**
 * Props for ControlledCheckbox component
 */
interface ControlledCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onCheckedChange?: (checked: boolean) => void;
  onFieldBlur?: (fieldName: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  id?: string;
  bgVariant?: ValidationPopoverBgT;
  children?: React.ReactNode;
}

/**
 * Controlled Checkbox component with React Hook Form integration
 *
 * @example
 * ```tsx
 * <ControlledCheckbox
 *   name="privacy"
 *   id="privacy"
 * />
 * ```
 */
export function ControlledCheckbox<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onCheckedChange,
  onFieldBlur,
  disabled,
  readOnly,
  className,
  id,
  bgVariant = "accent",
  children,
}: ControlledCheckboxProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "ControlledCheckbox must be used within a FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: actualControl,
  });

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      if (readOnly) return;
      field.onChange(checked);
      onCheckedChange?.(checked);
    },
    [field, onCheckedChange, readOnly]
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    onFieldBlur?.(name);
  }, [field, onFieldBlur, name]);

  const errorMessage = error?.message;
  const hasError = Boolean(errorMessage);
  const errorMessages = useMemo(() => {
    return errorMessage ? [errorMessage] : [];
  }, [errorMessage]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const checkboxId = id || name;

  const checkboxNode = (
    <div
      ref={wrapperRef}
      className={cn(
        "inline-flex items-center",
        children && "flex items-start space-x-2"
      )}
      role="checkbox"
      aria-checked={field.value ?? false}
      aria-invalid={hasError || undefined}
    >
      <Checkbox
        id={checkboxId}
        checked={field.value ?? false}
        onCheckedChange={handleCheckedChange}
        onBlur={handleBlur}
        disabled={disabled || readOnly}
        className={cn(
          "size-5 rounded-full",
          hasError && "border-destructive aria-invalid:border-destructive",
          className
        )}
        aria-invalid={hasError || undefined}
      />
      {children && (
        <label
          htmlFor={checkboxId}
          className={cn("cursor-pointer", hasError && "text-destructive")}
          onClick={(e) => {
            // Prevent popover from opening when clicking the label
            e.stopPropagation();
          }}
        >
          {children}
        </label>
      )}
    </div>
  );

  return (
    <ValidationPopover
      error={
        hasError && errorMessages.length > 0
          ? (errorMessages as (string | ValidationErrorT)[])
          : undefined
      }
      bgVariant={bgVariant}
      triggerRef={wrapperRef as React.Ref<HTMLButtonElement>}
      triggerOnHover={hasError && errorMessages.length > 0}
      className="-translate-x-24"
    >
      {checkboxNode}
    </ValidationPopover>
  );
}
